import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import EnterpriseAssetsPanel from '../components/EnterpriseAssetsPanel';
import {
  Buildings,
  Plus,
  PencilSimple,
  Trash,
  X,
  FloppyDisk,
  MapPin,
  HouseLine,
  CurrencyDollar,
  SwimmingPool,
  Brain,
  FileText,
  CheckCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = '/api';

const EMPTY_TYPOLOGY = {
  id: '',
  name: '',
  property_type: 'Apartamento',
  bedrooms: '',
  suites: '',
  bathrooms: '',
  parking_spaces: '',
  private_area_m2: '',
  balcony: false,
  garden: false,
  available: true,
  inventory_status: 'sob_consulta',
  min_price: '',
  max_price: '',
  description: '',
  plant_url: '',
};

const EMPTY_FAQ = {
  id: '',
  question: '',
  answer: '',
  active: true,
  priority: 0,
};

const EMPTY_MATERIAL = {
  id: '',
  name: '',
  material_type: 'folder',
  url: '',
  description: '',
  active: true,
  official: true,
};

const emptyForm = {
  name: '',
  slug: '',
  active: true,
  builder: '',
  property_type: 'Apartamento',
  status: 'ativo',

  city: '',
  state: '',
  neighborhood: '',
  address: '',
  map_url: '',

  delivery_forecast: '',
  construction_progress: '',

  min_price: '',
  max_price: '',

  description: '',
  commercial_description: '',
  link: '',

  typologies: [],
  amenities: [],
  differentials: [],
  nearby_places: [],

  financing: {
    accepted: true,
    mcmv: false,
    fgts: true,
    subsidy: false,
    minimum_income: '',
    minimum_entry: '',
    entry_installments: '',
    banks: [],
    notes: '',
  },

  inventory: {
    status: 'sob_consulta',
    total_units: '',
    available_units: '',
  },

  faq: [],
  materials: [],
  similar_enterprise_ids: [],
  reference_images: [],
  technical_materials: [],

  knowledge_base: {
    ideal_customer: '',
    main_sales_arguments: [],
    common_objections: [],
    important_notes: '',
    ai_summary: '',
  },

  ai_settings: {
    enabled: false,
    allow_price_answers: true,
    allow_inventory_answers: false,
    require_price_disclaimer: true,
    price_disclaimer:
      'Valores sujeitos à disponibilidade, atualização da tabela e análise das condições comerciais.',
    unknown_answer_policy: 'encaminhar_corretor',
  },
};

const tabs = [
  { id: 'general', label: 'Dados gerais', icon: Buildings },
  { id: 'product', label: 'Produto', icon: HouseLine },
  { id: 'commercial', label: 'Comercial', icon: CurrencyDollar },
  { id: 'structure', label: 'Estrutura', icon: SwimmingPool },
  { id: 'knowledge', label: 'Conhecimento', icon: Brain },
];

const toNumberOrNull = (value) => {
  if (value === '' || value === undefined || value === null) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const commaToArray = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const arrayToComma = (value) =>
  Array.isArray(value) ? value.join(', ') : '';


const parseMoneyValue = (value) => {
  if (
    value === '' ||
    value === undefined ||
    value === null
  ) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  let normalized = String(value)
    .replace(/\s/g, '')
    .replace(/R\$/gi, '')
    .replace(/[^\d,.-]/g, '');

  if (!normalized) return null;

  if (normalized.includes(',')) {
    normalized = normalized
      .replace(/\./g, '')
      .replace(',', '.');
  } else {
    normalized = normalized.replace(/,/g, '');
  }

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : null;
};

const formatMoneyField = (value) => {
  if (
    value === '' ||
    value === undefined ||
    value === null
  ) {
    return '';
  }

  const parsed = parseMoneyValue(value);

  if (parsed === null) return '';

  return parsed.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatMoney = (value) => {
  if (!value && value !== 0) return '-';

  return Number(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const Empreendimentos = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [selectedTab, setSelectedTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploading, setUploading] = useState(false);

  const [referenceFile, setReferenceFile] = useState(null);
  const [referenceName, setReferenceName] = useState('');
  const [uploadingReference, setUploadingReference] =
    useState(false);

  const [amenitiesText, setAmenitiesText] = useState('');
  const [differentialsText, setDifferentialsText] = useState('');
  const [banksText, setBanksText] = useState('');
  const [argumentsText, setArgumentsText] = useState('');
  const [objectionsText, setObjectionsText] = useState('');

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${API}/enterprises`, {
        withCredentials: true,
      });

      setItems(data || []);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar empreendimentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const updateNested = (group, field, value) => {
    setForm((current) => ({
      ...current,
      [group]: {
        ...(current[group] || {}),
        [field]: value,
      },
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setSelectedTab('general');
    setAmenitiesText('');
    setDifferentialsText('');
    setBanksText('');
    setArgumentsText('');
    setObjectionsText('');
    setUploadFile(null);
    setUploadName('');
    setReferenceFile(null);
    setReferenceName('');
  };

  const addTypology = () => {
    setForm((current) => ({
      ...current,
      typologies: [
        ...(current.typologies || []),
        {
          ...EMPTY_TYPOLOGY,
          id: crypto.randomUUID(),
        },
      ],
    }));
  };

  const updateTypology = (index, field, value) => {
    setForm((current) => ({
      ...current,
      typologies: current.typologies.map((item, itemIndex) =>
        itemIndex === index
          ? { ...item, [field]: value }
          : item
      ),
    }));
  };

  const removeTypology = (index) => {
    setForm((current) => ({
      ...current,
      typologies: current.typologies.filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));
  };

  const addFaq = () => {
    setForm((current) => ({
      ...current,
      faq: [
        ...(current.faq || []),
        {
          ...EMPTY_FAQ,
          id: crypto.randomUUID(),
        },
      ],
    }));
  };

  const updateFaq = (index, field, value) => {
    setForm((current) => ({
      ...current,
      faq: current.faq.map((item, itemIndex) =>
        itemIndex === index
          ? { ...item, [field]: value }
          : item
      ),
    }));
  };

  const removeFaq = (index) => {
    setForm((current) => ({
      ...current,
      faq: current.faq.filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));
  };

  const addMaterial = () => {
    setForm((current) => ({
      ...current,
      materials: [
        ...(current.materials || []),
        {
          ...EMPTY_MATERIAL,
          id: crypto.randomUUID(),
        },
      ],
    }));
  };

  const updateMaterial = (index, field, value) => {
    setForm((current) => ({
      ...current,
      materials: current.materials.map((item, itemIndex) =>
        itemIndex === index
          ? { ...item, [field]: value }
          : item
      ),
    }));
  };

  const removeMaterial = (index) => {
    setForm((current) => ({
      ...current,
      materials: current.materials.filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));
  };

  const buildPayload = () => ({
    ...form,

    construction_progress: toNumberOrNull(
      form.construction_progress
    ),
    min_price: parseMoneyValue(form.min_price),
    max_price: parseMoneyValue(form.max_price),

    amenities: commaToArray(amenitiesText),
    differentials: commaToArray(differentialsText),

    typologies: (form.typologies || []).map((item) => ({
      ...item,
      bedrooms: toNumberOrNull(item.bedrooms),
      suites: toNumberOrNull(item.suites),
      bathrooms: toNumberOrNull(item.bathrooms),
      parking_spaces: toNumberOrNull(item.parking_spaces),
      private_area_m2: toNumberOrNull(item.private_area_m2),
      min_price: parseMoneyValue(item.min_price),
      max_price: parseMoneyValue(item.max_price),
    })),

    financing: {
      ...form.financing,
      minimum_income: parseMoneyValue(
        form.financing.minimum_income
      ),
      minimum_entry: parseMoneyValue(
        form.financing.minimum_entry
      ),
      entry_installments: toNumberOrNull(
        form.financing.entry_installments
      ),
      banks: commaToArray(banksText),
    },

    inventory: {
      ...form.inventory,
      total_units: toNumberOrNull(
        form.inventory.total_units
      ),
      available_units: toNumberOrNull(
        form.inventory.available_units
      ),
    },

    knowledge_base: {
      ...form.knowledge_base,
      main_sales_arguments: commaToArray(argumentsText),
      common_objections: commaToArray(objectionsText),
    },
  });


  const uploadMaterialFile = async () => {
    if (!editingId) {
      toast.error(
        'Salve o empreendimento antes de enviar arquivos'
      );
      return;
    }

    if (!uploadFile) {
      toast.error('Selecione um arquivo');
      return;
    }

    const allowedExtensions = [
      '.pdf',
      '.jpg',
      '.jpeg',
      '.png',
      '.mp4',
    ];

    const lowerName = uploadFile.name.toLowerCase();

    if (
      !allowedExtensions.some((extension) =>
        lowerName.endsWith(extension)
      )
    ) {
      toast.error(
        'Utilize arquivos PDF, JPG, JPEG, PNG ou MP4'
      );
      return;
    }

    if (uploadFile.size > 100 * 1024 * 1024) {
      toast.error(
        'O arquivo ultrapassa o limite de 100 MB'
      );
      return;
    }

    setUploading(true);

    try {
      const data = new FormData();

      data.append('file', uploadFile);
      data.append(
        'name',
        uploadName.trim() || uploadFile.name
      );

      const response = await axios.post(
        `${API}/enterprises/${editingId}/materials/upload`,
        data,
        {
          withCredentials: true,
        }
      );

      setForm((current) => ({
        ...current,
        materials: response.data?.materials || [],
      }));

      setUploadFile(null);
      setUploadName('');

      const input = document.getElementById(
        'enterprise-material-upload'
      );

      if (input) {
        input.value = '';
      }

      toast.success('Material enviado com sucesso');
      await fetchItems();
    } catch (error) {
      console.error(error);

      const detail = error?.response?.data?.detail;

      toast.error(
        typeof detail === 'string'
          ? detail
          : detail
            ? JSON.stringify(detail)
            : `Erro ao enviar material${
                error?.response?.status
                  ? ` — HTTP ${error.response.status}`
                  : ''
              }`
      );
    } finally {
      setUploading(false);
    }
  };

  const deleteUploadedMaterial = async (material) => {
    if (!editingId) return;

    if (
      !window.confirm(
        `Excluir o material "${material.name}"?`
      )
    ) {
      return;
    }

    try {
      const { data } = await axios.delete(
        `${API}/enterprises/${editingId}/materials/${material.id}`,
        { withCredentials: true }
      );

      setForm((current) => ({
        ...current,
        materials: data?.materials || [],
      }));

      toast.success('Material excluído');
      await fetchItems();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        'Erro ao excluir material'
      );
    }
  };

  const toggleSimilarEnterprise = (enterpriseId) => {
    setForm((current) => {
      const selected =
        current.similar_enterprise_ids?.includes(
          enterpriseId
        );

      return {
        ...current,
        similar_enterprise_ids: selected
          ? current.similar_enterprise_ids.filter(
              (id) => id !== enterpriseId
            )
          : [
              ...(current.similar_enterprise_ids || []),
              enterpriseId,
            ],
      };
    });
  };


  const uploadReferenceImage = async () => {
    if (!editingId) {
      toast.error(
        'Salve o empreendimento antes de enviar imagens'
      );
      return;
    }

    if (!referenceFile) {
      toast.error('Selecione uma imagem');
      return;
    }

    const lowerName = referenceFile.name.toLowerCase();

    if (
      !['.jpg', '.jpeg', '.png'].some(
        (extension) => lowerName.endsWith(extension)
      )
    ) {
      toast.error('Utilize imagens JPG, JPEG ou PNG');
      return;
    }

    if (referenceFile.size > 15 * 1024 * 1024) {
      toast.error(
        'A imagem ultrapassa o limite de 15 MB'
      );
      return;
    }

    setUploadingReference(true);

    try {
      const data = new FormData();

      data.append('file', referenceFile);
      data.append(
        'name',
        referenceName.trim() || referenceFile.name
      );

      const response = await axios.post(
        `${API}/enterprises/${editingId}/reference-images/upload`,
        data,
        {
          withCredentials: true,
        }
      );

      setForm((current) => ({
        ...current,
        reference_images:
          response.data?.reference_images || [],
      }));

      setReferenceFile(null);
      setReferenceName('');

      const input = document.getElementById(
        'enterprise-reference-upload'
      );

      if (input) input.value = '';

      toast.success(
        'Imagem de referência enviada com sucesso'
      );

      await fetchItems();
    } catch (error) {
      const detail = error?.response?.data?.detail;

      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Erro ao enviar imagem de referência'
      );
    } finally {
      setUploadingReference(false);
    }
  };

  const deleteReferenceImage = async (image) => {
    if (!editingId) return;

    if (
      !window.confirm(
        `Excluir a imagem "${image.name}"?`
      )
    ) {
      return;
    }

    try {
      const { data } = await axios.delete(
        `${API}/enterprises/${editingId}/reference-images/${image.id}`,
        { withCredentials: true }
      );

      setForm((current) => ({
        ...current,
        reference_images:
          data?.reference_images || [],
      }));

      toast.success('Imagem excluída');
      await fetchItems();
    } catch (error) {
      toast.error('Erro ao excluir imagem');
    }
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      toast.error('Informe o nome do empreendimento');
      return;
    }

    setSaving(true);

    try {
      const payload = buildPayload();

      if (editingId) {
        await axios.patch(
          `${API}/enterprises/${editingId}`,
          payload,
          { withCredentials: true }
        );

        toast.success('Empreendimento atualizado');
      } else {
        await axios.post(
          `${API}/enterprises`,
          payload,
          { withCredentials: true }
        );

        toast.success('Empreendimento cadastrado');
      }

      resetForm();
      await fetchItems();
    } catch (error) {
      console.error(error);

      toast.error(
        error?.response?.data?.detail ||
        'Erro ao salvar empreendimento'
      );
    } finally {
      setSaving(false);
    }
  };

  const edit = (item) => {
    const merged = {
      ...emptyForm,
      ...item,
      financing: {
        ...emptyForm.financing,
        ...(item.financing || {}),
      },
      inventory: {
        ...emptyForm.inventory,
        ...(item.inventory || {}),
      },
      knowledge_base: {
        ...emptyForm.knowledge_base,
        ...(item.knowledge_base || {}),
      },
      ai_settings: {
        ...emptyForm.ai_settings,
        ...(item.ai_settings || {}),
      },
      typologies: item.typologies || [],
      faq: item.faq || [],
      materials: item.materials || [],
      reference_images: item.reference_images || [],
      technical_materials:
        item.technical_materials || [],
    };

    setEditingId(item.id);
    setForm(merged);

    setAmenitiesText(arrayToComma(item.amenities));
    setDifferentialsText(arrayToComma(item.differentials));
    setBanksText(arrayToComma(item.financing?.banks));
    setArgumentsText(
      arrayToComma(
        item.knowledge_base?.main_sales_arguments
      )
    );
    setObjectionsText(
      arrayToComma(
        item.knowledge_base?.common_objections
      )
    );

    setSelectedTab('general');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const archive = async (item) => {
    if (
      !window.confirm(
        `Arquivar o empreendimento "${item.name}"?\n\n` +
        'Os leads e históricos existentes serão preservados.'
      )
    ) {
      return;
    }

    try {
      await axios.delete(
        `${API}/enterprises/${item.id}`,
        { withCredentials: true }
      );

      toast.success('Empreendimento arquivado');
      await fetchItems();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        'Erro ao arquivar empreendimento'
      );
    }
  };

  const activeItems = useMemo(
    () => items.filter((item) => !item.archived),
    [items]
  );

  return (
    <Layout>
      <div className="space-y-6">
        <header className="flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-emerald-100 text-emerald-700">
            <Buildings size={30} weight="duotone" />
          </div>

          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Empreendimentos
            </h1>
            <p className="text-slate-600 mt-1">
              Produtos, informações comerciais e conhecimento oficial para a IA.
            </p>
          </div>
        </header>

        <form
          onSubmit={submit}
          className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
        >
          <div className="p-6 border-b border-slate-200 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {editingId
                  ? 'Editar empreendimento'
                  : 'Novo empreendimento'}
              </h2>

              <p className="text-sm text-slate-500 mt-1">
                Preencha gradualmente. Os campos não preenchidos não serão inventados pela IA.
              </p>
            </div>

            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="p-2 rounded-lg hover:bg-slate-100"
              >
                <X size={22} />
              </button>
            )}
          </div>

          <div className="border-b border-slate-200 overflow-x-auto">
            <div className="flex min-w-max px-4">
              {tabs.map((tab) => {
                const Icon = tab.icon;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSelectedTab(tab.id)}
                    className={`flex items-center gap-2 px-5 py-4 border-b-2 text-sm font-semibold ${
                      selectedTab === tab.id
                        ? 'border-emerald-600 text-emerald-700'
                        : 'border-transparent text-slate-500'
                    }`}
                  >
                    <Icon size={19} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-6">
            {selectedTab === 'general' && (
              <GeneralTab
                form={form}
                updateField={updateField}
              />
            )}

            {selectedTab === 'product' && (
              <ProductTab
                form={form}
                updateField={updateField}
                addTypology={addTypology}
                updateTypology={updateTypology}
                removeTypology={removeTypology}
              />
            )}

            {selectedTab === 'commercial' && (
              <CommercialTab
                form={form}
                updateField={updateField}
                updateNested={updateNested}
                banksText={banksText}
                setBanksText={setBanksText}
              />
            )}

            {selectedTab === 'structure' && (
              <StructureTab
                amenitiesText={amenitiesText}
                setAmenitiesText={setAmenitiesText}
                differentialsText={differentialsText}
                setDifferentialsText={setDifferentialsText}
                form={form}
                updateField={updateField}
              />
            )}

            {selectedTab === 'knowledge' && (
              <KnowledgeTab
                form={form}
                setForm={setForm}
                fetchItems={fetchItems}
                updateNested={updateNested}
                argumentsText={argumentsText}
                setArgumentsText={setArgumentsText}
                objectionsText={objectionsText}
                setObjectionsText={setObjectionsText}
                addFaq={addFaq}
                updateFaq={updateFaq}
                removeFaq={removeFaq}
                addMaterial={addMaterial}
                updateMaterial={updateMaterial}
                removeMaterial={removeMaterial}
                items={items}
                editingId={editingId}
                uploadFile={uploadFile}
                setUploadFile={setUploadFile}
                uploadName={uploadName}
                setUploadName={setUploadName}
                uploading={uploading}
                uploadMaterialFile={uploadMaterialFile}
                deleteUploadedMaterial={
                  deleteUploadedMaterial
                }
                toggleSimilarEnterprise={
                  toggleSimilarEnterprise
                }
                referenceFile={referenceFile}
                setReferenceFile={setReferenceFile}
                referenceName={referenceName}
                setReferenceName={setReferenceName}
                uploadingReference={uploadingReference}
                uploadReferenceImage={uploadReferenceImage}
                deleteReferenceImage={deleteReferenceImage}
              />
            )}
          </div>

          <div className="px-6 py-5 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-5 py-3 rounded-xl border border-slate-300 text-slate-700 font-semibold"
              >
                Cancelar
              </button>
            )}

            <button
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60"
            >
              <FloppyDisk size={20} />
              {saving
                ? 'Salvando...'
                : editingId
                  ? 'Salvar alterações'
                  : 'Cadastrar empreendimento'}
            </button>
          </div>
        </form>

        <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-200">
            <h2 className="text-lg font-bold text-slate-900">
              Empreendimentos cadastrados
            </h2>
          </div>

          {loading ? (
            <div className="p-10 text-center text-slate-500">
              Carregando...
            </div>
          ) : activeItems.length === 0 ? (
            <div className="p-10 text-center text-slate-500">
              Nenhum empreendimento cadastrado.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {activeItems.map((item) => (
                <div
                  key={item.id}
                  className="p-5 flex flex-col xl:flex-row xl:items-center justify-between gap-5"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-bold text-slate-900">
                        {item.name}
                      </h3>

                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 uppercase">
                        {item.status}
                      </span>

                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        (item.knowledge_completion || 0) >= 70
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        Base IA: {item.knowledge_completion || 0}%
                      </span>
                    </div>

                    <p className="text-sm text-slate-600 mt-2">
                      {item.city || 'Cidade não informada'}
                      {item.state ? `/${item.state}` : ''}
                      {' • '}
                      {item.builder || 'Construtora não informada'}
                      {' • '}
                      {item.property_type || '-'}
                    </p>

                    <p className="text-sm text-emerald-700 mt-1">
                      {formatMoney(item.min_price)}
                      {' até '}
                      {formatMoney(item.max_price)}
                    </p>

                    <p className="text-xs text-slate-500 mt-2">
                      {item.typologies?.length || 0} tipologia(s)
                      {' • '}
                      {item.amenities?.length || 0} item(ns) de lazer
                      {' • '}
                      {item.faq?.length || 0} pergunta(s)
                      {' • '}
                      {item.materials?.length || 0} material(is)
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => edit(item)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50"
                    >
                      <PencilSimple size={18} />
                      Editar
                    </button>

                    <button
                      onClick={() => archive(item)}
                      className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                      title="Arquivar"
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
};

const GeneralTab = ({ form, updateField }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
    <Field label="Nome do empreendimento *">
      <input
        value={form.name}
        onChange={(event) =>
          updateField('name', event.target.value)
        }
        className="input-default"
      />
    </Field>

    <Field label="Construtora/Incorporadora">
      <input
        value={form.builder}
        onChange={(event) =>
          updateField('builder', event.target.value)
        }
        className="input-default"
      />
    </Field>

    <Field label="Status">
      <select
        value={form.status}
        onChange={(event) =>
          updateField('status', event.target.value)
        }
        className="input-default"
      >
        <option value="ativo">Ativo</option>
        <option value="lancamento">Lançamento</option>
        <option value="em_obras">Em obras</option>
        <option value="pronto">Pronto</option>
        <option value="pausado">Pausado</option>
        <option value="encerrado">Encerrado</option>
      </select>
    </Field>

    <Field label="Cidade">
      <input
        value={form.city}
        onChange={(event) =>
          updateField('city', event.target.value)
        }
        className="input-default"
      />
    </Field>

    <Field label="Estado">
      <input
        value={form.state}
        onChange={(event) =>
          updateField('state', event.target.value.toUpperCase())
        }
        maxLength={2}
        className="input-default"
        placeholder="PE"
      />
    </Field>

    <Field label="Bairro">
      <input
        value={form.neighborhood}
        onChange={(event) =>
          updateField('neighborhood', event.target.value)
        }
        className="input-default"
      />
    </Field>

    <Field label="Endereço" span="md:col-span-2">
      <input
        value={form.address}
        onChange={(event) =>
          updateField('address', event.target.value)
        }
        className="input-default"
      />
    </Field>

    <Field label="Previsão de entrega">
      <input
        value={form.delivery_forecast}
        onChange={(event) =>
          updateField(
            'delivery_forecast',
            event.target.value
          )
        }
        className="input-default"
        placeholder="Dezembro de 2027"
      />
    </Field>

    <Field label="Descrição comercial" span="md:col-span-3">
      <textarea
        value={
          form.commercial_description ||
          form.description
        }
        onChange={(event) =>
          updateField(
            'commercial_description',
            event.target.value
          )
        }
        className="input-default min-h-[120px]"
      />
    </Field>
  </div>
);

const ProductTab = ({
  form,
  updateField,
  addTypology,
  updateTypology,
  removeTypology,
}) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      <Field label="Tipo principal">
        <select
          value={form.property_type}
          onChange={(event) =>
            updateField(
              'property_type',
              event.target.value
            )
          }
          className="input-default"
        >
          <option>Apartamento</option>
          <option>Casa</option>
          <option>Lote</option>
          <option>Comercial</option>
          <option>Misto</option>
        </select>
      </Field>

      <Field label="Andamento da obra (%)">
        <input
          type="number"
          min="0"
          max="100"
          value={form.construction_progress}
          onChange={(event) =>
            updateField(
              'construction_progress',
              event.target.value
            )
          }
          className="input-default"
        />
      </Field>
    </div>

    <div className="flex items-center justify-between">
      <div>
        <h3 className="font-bold text-slate-900">
          Tipologias
        </h3>
        <p className="text-sm text-slate-500">
          Cadastre cada planta separadamente.
        </p>
      </div>

      <button
        type="button"
        onClick={addTypology}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold"
      >
        <Plus size={18} />
        Adicionar tipologia
      </button>
    </div>

    {(form.typologies || []).map((item, index) => (
      <div
        key={item.id || index}
        className="p-5 rounded-xl border border-slate-200 bg-slate-50"
      >
        <div className="flex justify-between mb-4">
          <h4 className="font-semibold">
            Tipologia {index + 1}
          </h4>

          <button
            type="button"
            onClick={() => removeTypology(index)}
            className="text-red-600"
          >
            <Trash size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Nome" span="col-span-2">
            <input
              value={item.name}
              onChange={(event) =>
                updateTypology(
                  index,
                  'name',
                  event.target.value
                )
              }
              className="input-default"
              placeholder="2 quartos com suíte"
            />
          </Field>

          <NumberField
            label="Quartos"
            value={item.bedrooms}
            onChange={(value) =>
              updateTypology(index, 'bedrooms', value)
            }
          />

          <NumberField
            label="Suítes"
            value={item.suites}
            onChange={(value) =>
              updateTypology(index, 'suites', value)
            }
          />

          <NumberField
            label="Banheiros"
            value={item.bathrooms}
            onChange={(value) =>
              updateTypology(index, 'bathrooms', value)
            }
          />

          <NumberField
            label="Vagas"
            value={item.parking_spaces}
            onChange={(value) =>
              updateTypology(
                index,
                'parking_spaces',
                value
              )
            }
          />

          <NumberField
            label="Área privativa (m²)"
            value={item.private_area_m2}
            onChange={(value) =>
              updateTypology(
                index,
                'private_area_m2',
                value
              )
            }
          />

          <MoneyField
            label="Valor inicial"
            value={item.min_price}
            onChange={(value) =>
              updateTypology(index, 'min_price', value)
            }
          />

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(item.balcony)}
              onChange={(event) =>
                updateTypology(
                  index,
                  'balcony',
                  event.target.checked
                )
              }
            />
            Varanda
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(item.available)}
              onChange={(event) =>
                updateTypology(
                  index,
                  'available',
                  event.target.checked
                )
              }
            />
            Disponível
          </label>
        </div>
      </div>
    ))}

    {form.typologies.length === 0 && (
      <div className="p-8 rounded-xl border border-dashed text-center text-slate-500">
        Nenhuma tipologia cadastrada.
      </div>
    )}
  </div>
);

const CommercialTab = ({
  form,
  updateField,
  updateNested,
  banksText,
  setBanksText,
}) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      <MoneyField
        label="Valor inicial"
        value={form.min_price}
        onChange={(value) =>
          updateField('min_price', value)
        }
      />

      <MoneyField
        label="Valor máximo"
        value={form.max_price}
        onChange={(value) =>
          updateField('max_price', value)
        }
      />

      <Field label="Disponibilidade">
        <select
          value={form.inventory.status}
          onChange={(event) =>
            updateNested(
              'inventory',
              'status',
              event.target.value
            )
          }
          className="input-default"
        >
          <option value="disponivel">Disponível</option>
          <option value="poucas_unidades">
            Poucas unidades
          </option>
          <option value="sob_consulta">
            Sob consulta
          </option>
          <option value="esgotado">Esgotado</option>
        </select>
      </Field>

      <MoneyField
        label="Entrada mínima"
        value={form.financing.minimum_entry}
        onChange={(value) =>
          updateNested(
            'financing',
            'minimum_entry',
            value
          )
        }
      />

      <MoneyField
        label="Renda mínima estimada"
        value={form.financing.minimum_income}
        onChange={(value) =>
          updateNested(
            'financing',
            'minimum_income',
            value
          )
        }
      />

      <NumberField
        label="Parcelas da entrada"
        value={form.financing.entry_installments}
        onChange={(value) =>
          updateNested(
            'financing',
            'entry_installments',
            value
          )
        }
      />
    </div>

    <div className="flex flex-wrap gap-5">
      {[
        ['accepted', 'Aceita financiamento'],
        ['mcmv', 'Minha Casa Minha Vida'],
        ['fgts', 'Permite uso de FGTS'],
        ['subsidy', 'Possibilidade de subsídio'],
      ].map(([field, label]) => (
        <label
          key={field}
          className="flex items-center gap-2 text-sm"
        >
          <input
            type="checkbox"
            checked={Boolean(form.financing[field])}
            onChange={(event) =>
              updateNested(
                'financing',
                field,
                event.target.checked
              )
            }
          />
          {label}
        </label>
      ))}
    </div>

    <Field label="Bancos" helper="Separe por vírgulas">
      <input
        value={banksText}
        onChange={(event) =>
          setBanksText(event.target.value)
        }
        className="input-default"
        placeholder="Caixa, Banco do Brasil"
      />
    </Field>

    <Field label="Observações comerciais">
      <textarea
        value={form.financing.notes}
        onChange={(event) =>
          updateNested(
            'financing',
            'notes',
            event.target.value
          )
        }
        className="input-default min-h-[100px]"
      />
    </Field>
  </div>
);

const StructureTab = ({
  amenitiesText,
  setAmenitiesText,
  differentialsText,
  setDifferentialsText,
  form,
  updateField,
}) => (
  <div className="space-y-6">
    <Field
      label="Lazer e estrutura"
      helper="Separe por vírgulas"
    >
      <textarea
        value={amenitiesText}
        onChange={(event) =>
          setAmenitiesText(event.target.value)
        }
        className="input-default min-h-[100px]"
        placeholder="Piscina, academia, salão de festas..."
      />
    </Field>

    <Field
      label="Diferenciais"
      helper="Separe por vírgulas"
    >
      <textarea
        value={differentialsText}
        onChange={(event) =>
          setDifferentialsText(event.target.value)
        }
        className="input-default min-h-[100px]"
        placeholder="Localização, segurança, valorização..."
      />
    </Field>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Field label="Link do mapa">
        <input
          value={form.map_url}
          onChange={(event) =>
            updateField('map_url', event.target.value)
          }
          className="input-default"
          placeholder="https://maps.google.com/..."
        />
      </Field>

      <Field label="Página pública">
        <input
          value={form.link}
          onChange={(event) =>
            updateField('link', event.target.value)
          }
          className="input-default"
          placeholder="https://..."
        />
      </Field>
    </div>
  </div>
);

const KnowledgeTab = ({
  form,
  setForm,
  fetchItems,
  updateNested,
  argumentsText,
  setArgumentsText,
  objectionsText,
  setObjectionsText,
  addFaq,
  updateFaq,
  removeFaq,
  addMaterial,
  updateMaterial,
  removeMaterial,
  items,
  editingId,
  uploadFile,
  setUploadFile,
  uploadName,
  setUploadName,
  uploading,
  uploadMaterialFile,
  deleteUploadedMaterial,
  toggleSimilarEnterprise,
  referenceFile,
  setReferenceFile,
  referenceName,
  setReferenceName,
  uploadingReference,
  uploadReferenceImage,
  deleteReferenceImage,
}) => (
  <div className="space-y-8">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Field label="Público ideal">
        <textarea
          value={form.knowledge_base.ideal_customer}
          onChange={(event) =>
            updateNested(
              'knowledge_base',
              'ideal_customer',
              event.target.value
            )
          }
          className="input-default min-h-[100px]"
        />
      </Field>

      <Field label="Resumo oficial para a IA">
        <textarea
          value={form.knowledge_base.ai_summary}
          onChange={(event) =>
            updateNested(
              'knowledge_base',
              'ai_summary',
              event.target.value
            )
          }
          className="input-default min-h-[100px]"
        />
      </Field>
    </div>

    <Field
      label="Principais argumentos comerciais"
      helper="Separe por vírgulas"
    >
      <textarea
        value={argumentsText}
        onChange={(event) =>
          setArgumentsText(event.target.value)
        }
        className="input-default min-h-[90px]"
      />
    </Field>

    <Field
      label="Objeções frequentes"
      helper="Nesta etapa registre apenas os temas. A forma de resposta será definida no Sales DNA."
    >
      <textarea
        value={objectionsText}
        onChange={(event) =>
          setObjectionsText(event.target.value)
        }
        className="input-default min-h-[90px]"
      />
    </Field>

    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold">Informações frequentes</h3>
          <p className="text-sm text-slate-500">
            Conteúdo factual. O estilo de resposta será tratado no Sales DNA.
          </p>
        </div>

        <button
          type="button"
          onClick={addFaq}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold"
        >
          <Plus size={18} />
          Adicionar informação
        </button>
      </div>

      <div className="space-y-4">
        {(form.faq || []).map((item, index) => (
          <div
            key={item.id || index}
            className="p-4 rounded-xl border bg-slate-50"
          >
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => removeFaq(index)}
                className="text-red-600"
              >
                <Trash size={17} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Pergunta ou tema">
                <input
                  value={item.question}
                  onChange={(event) =>
                    updateFaq(
                      index,
                      'question',
                      event.target.value
                    )
                  }
                  className="input-default"
                />
              </Field>

              <Field label="Informação oficial">
                <textarea
                  value={item.answer}
                  onChange={(event) =>
                    updateFaq(
                      index,
                      'answer',
                      event.target.value
                    )
                  }
                  className="input-default min-h-[80px]"
                />
              </Field>
            </div>
          </div>
        ))}
      </div>
    </div>

    <EnterpriseAssetsPanel
      enterpriseId={editingId}
      form={form}
      setForm={setForm}
      refreshEnterprises={fetchItems}
    />

    <label className="flex items-center justify-between gap-4 p-4 rounded-xl bg-indigo-50 border border-indigo-200">
      <div>
        <p className="font-semibold text-indigo-900">
          Disponibilizar base para a IA
        </p>
        <p className="text-sm text-indigo-700">
          A integração real será ativada na próxima etapa.
        </p>
      </div>

      <input
        type="checkbox"
        checked={Boolean(form.ai_settings.enabled)}
        onChange={(event) =>
          updateNested(
            'ai_settings',
            'enabled',
            event.target.checked
          )
        }
        className="w-5 h-5"
      />
    </label>
  </div>
);

const Field = ({
  label,
  helper,
  children,
  span = '',
}) => (
  <div className={span}>
    <label className="block text-sm font-medium text-slate-700 mb-2">
      {label}
    </label>

    {children}

    {helper && (
      <p className="text-xs text-slate-500 mt-2">
        {helper}
      </p>
    )}
  </div>
);

const MoneyField = ({
  label,
  value,
  onChange,
}) => {
  const [displayValue, setDisplayValue] = React.useState(
    formatMoneyField(value)
  );

  const focusedRef = React.useRef(false);

  React.useEffect(() => {
    if (!focusedRef.current) {
      setDisplayValue(formatMoneyField(value));
    }
  }, [value]);

  const handleFocus = (event) => {
    focusedRef.current = true;

    // Facilita substituir completamente o valor atual.
    window.setTimeout(() => {
      event.target.select();
    }, 0);
  };

  const handleChange = (event) => {
    // Durante a digitação não reformata e não reposiciona o cursor.
    setDisplayValue(event.target.value);
  };

  const handleBlur = () => {
    focusedRef.current = false;

    const parsed = parseMoneyValue(displayValue);

    if (parsed === null) {
      setDisplayValue('');
      onChange('');
      return;
    }

    onChange(parsed);
    setDisplayValue(formatMoneyField(parsed));
  };

  return (
    <Field label={label}>
      <input
        type="text"
        inputMode="decimal"
        value={displayValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        className="input-default"
        placeholder="265.900,00"
      />
    </Field>
  );
};


const NumberField = ({
  label,
  value,
  onChange,
}) => (
  <Field label={label}>
    <input
      type="number"
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value)}
      className="input-default"
    />
  </Field>
);

export default Empreendimentos;
